import { useEffect, useState } from "react";
import { subscribeKey } from "valtio/utils";
import { appStore } from "@store/appStore";
import { FeatureFlagService } from "@services/featureFlags/FeatureFlagService";
import type { FeatureFlags } from "@services/featureFlags/commands/cmdListFeatureFlags";

/** The feature flags the web app reads (MPN-2014). The codes are declared on the server (MPN-1963). */
export const AD_COPY_AD_TOKENS = "ad-copy-ad-tokens";
export const STUDIO_AD_TOKENS = "studio-ad-tokens";
export const REUPLOAD_AUDIENCE_MEMBERS = "reupload-audience-members";
export const CHANNEL_HOLDOUT = "channel-holdout";
export const AD_GROUP_NETWORKS = "ad-group-networks";
/** The Brand Identity page (MPN-1712). Declared on the server in MPN-2020. */
export const BRAND_IDENTITY_V2 = "brand-identity-v2";
/** The OP launch list and read-only launch detail pages, read from the OP warehouse (MPN-2229). */
export const OP_LAUNCH_LIST = "op-launch-list";
/** The OP Launch Dashboard, read from the OP warehouse (MPN-2230). */
export const OP_LAUNCH_DASHBOARD = "op-launch-dashboard";
/** Admin > Banners and the Partner Dashboard marketing banner (MPN-2171). Declared on the server in MPN-2264. */
export const MARKETING_BANNERS = "marketing-banners";
/** The OP Reports pages: the preserved Periscope reports (MPN-2275). */
export const OP_REPORTS = "op-reports";

export type FeatureFlagCode =
    | typeof AD_COPY_AD_TOKENS
    | typeof STUDIO_AD_TOKENS
    | typeof REUPLOAD_AUDIENCE_MEMBERS
    | typeof CHANNEL_HOLDOUT
    | typeof AD_GROUP_NETWORKS
    | typeof BRAND_IDENTITY_V2
    | typeof OP_LAUNCH_LIST
    | typeof OP_LAUNCH_DASHBOARD
    | typeof MARKETING_BANNERS
    | typeof OP_REPORTS;

/**
 * The flags, fetched once for the whole page and shared by every caller -- the same shape as
 * useAdTokens. The promise is the cache, so callers mounting in the same tick share one request.
 */
let flags: Promise<FeatureFlags> | null = null;
/** What the request said, once it has said it, so a later mount starts on the right answer. */
let known: FeatureFlags | null = null;
/**
 * The token the cached answer was read with. The router asks for the flags before anyone has
 * signed in, so on a fresh browser that first read is a 401 and every flag-gated route stayed
 * off until a full reload: the sidebar showed OP Launches, the click went nowhere. A read made
 * with a different token is a different question, so it is asked again.
 */
let readWith: string | null = null;

const currentToken = () => appStore.authToken ?? "";

/**
 * The signed-in token, re-read when it changes. Not useSnapshot: some tests stand in a plain
 * object for the store, and the hook must still work there (it then never re-reads).
 */
function useAuthToken(): string {
    const [token, setToken] = useState(currentToken);
    useEffect(() => {
        try {
            return subscribeKey(appStore, "authToken", () => setToken(currentToken()));
        } catch {
            return undefined;
        }
    }, []);
    return token;
}

/** A failed read is forgotten, like useAdTokens, so the next mount asks again. */
export function loadFeatureFlags(token: string = currentToken()): Promise<FeatureFlags> {
    if (!flags || readWith !== token) {
        readWith = token;
        known = null;
        const attempt: Promise<FeatureFlags> = Promise.resolve()
            .then(() => new FeatureFlagService().listFeatureFlags())
            .then((result) => (known = result))
            .catch((error: unknown) => {
                if (flags === attempt) flags = null;
                throw error;
            });
        flags = attempt;
    }
    return flags;
}

/**
 * Drops the cached flags. For tests, which may pass the flags to start from instead of
 * fetching them -- nothing in the app refetches.
 */
export function resetFeatureFlags(start?: Partial<Record<FeatureFlagCode, boolean>>): void {
    known = start ? { ...start } : null;
    flags = known ? Promise.resolve(known) : null;
    readWith = currentToken();
}

/**
 * Whether a feature is on. Off until the flags arrive, and off if they never do or do not
 * name this one: a new feature stays hidden unless the server says otherwise. Signing in
 * (the token changing) asks again, so a read made before sign-in does not stick.
 */
export function useFeatureFlag(code: FeatureFlagCode): boolean {
    const authToken = useAuthToken();
    const [on, setOn] = useState(() => known?.[code] === true);

    useEffect(() => {
        let live = true;
        loadFeatureFlags(authToken).then(
            (result) => {
                if (live) setOn(result[code] === true);
            },
            () => {
                if (live) setOn(false);
            },
        );
        return () => {
            live = false;
        };
    }, [code, authToken]);

    return on;
}
